import { PowerPerformanceForms, type FundCategory, type FundNature } from '../../../../../types/mutual-fund-detail.types'
import type { RebalanceSuggestion } from '../../../types/recommendation.types'
import { roundOffAndFormatNumberWithCurrency } from '../../../../../utils/utils'
import { StepHeading } from '../../../components/plan/StepHeading'
import { ReinvestFundCard } from '../../../components/plan/ReinvestFundCard'
import { RecommendedRebalancingStrip } from '../../../components/plan/RecommendedRebalancingStrip'
import { ELSSCallout } from '../../../components/plan/ELSSCallout'
import { getMutualFundDetailCTA } from '../../../../../utils/mutualFundDetail'
import { postCTA } from '../../../../../utils/appBridge'
import { useBottomSheet } from '../../../../../state-management/contexts/BottomSheetContext'
import ReinvestLowerAmountBottomSheet from '../../../bottom-sheets/ReinvestLowerAmountBottomSheet'
import {
  TaxHarvestingScreenName,
  TaxHarvestingEventId,
  trackTaxHarvestingUserClicked
} from '../../../tracking/taxHarvestingTracking'

type ReinvestOption = {
  fund: Partial<RebalanceSuggestion>
  isRecommended: boolean
}

type CurrentFundData = {
  isin?: string
  name?: string
  nature?: FundNature
  category?: FundCategory
  logo?: string
  powerRank?: number
  powerRating?: RebalanceSuggestion['performanceRating']
}

type ReinvestSectionProps = {
  stepNumber: number
  totalWithdrawAmount: number
  rebalanceSuggestions: RebalanceSuggestion[]
  directFundSuggestion: RebalanceSuggestion | null
  currentFundData: CurrentFundData
  isElss: boolean
}

const SUBTEXT_STYLE = 'font-weight: 400; color: #6A6E6E;'

function getReinvestHeading(
  reinvestAmountStr: string,
  hasDirect: boolean,
  hasRebalance: boolean
): string {
  if (hasRebalance) {
    return `Reinvest ${reinvestAmountStr} immediately <span style="${SUBTEXT_STYLE}"> in better performing fund </span>`
  }
  if (hasDirect) {
    return `Reinvest ${reinvestAmountStr} immediately <span style="${SUBTEXT_STYLE}"> in direct plan of the same fund </span>`
  }
  return `Reinvest ${reinvestAmountStr} immediately <span style="${SUBTEXT_STYLE}"> in the same fund </span>`
}

function toSameFundSuggestion(current: CurrentFundData): Partial<RebalanceSuggestion> {
  return {
    isin: current.isin ?? '',
    name: current.name ?? '',
    fundNature: (current.nature ?? '') as RebalanceSuggestion['fundNature'],
    fundCategory: (current.category ?? '') as RebalanceSuggestion['fundCategory'],
    logo: current.logo ?? '',
    powerRank: current.powerRank ?? 0,
    performanceRating: (current.powerRating ?? '') as RebalanceSuggestion['performanceRating'],
  }
}

function getAlternativeHintText(powerRating?: RebalanceSuggestion['performanceRating']): string | null {
  if (powerRating === PowerPerformanceForms.OFF_TRACK) {
    return 'This fund is off-track. Consider switching to a top-ranked fund in this category.'
  }
  if (powerRating === PowerPerformanceForms.OUT_OF_FORM) {
    return 'This fund is out-of-form. Consider switching to a top-ranked fund in this category.'
  }
  return null
}

export function ReinvestSection({
  stepNumber,
  totalWithdrawAmount,
  rebalanceSuggestions,
  directFundSuggestion,
  currentFundData,
  isElss,
}: ReinvestSectionProps) {
  const { openBottomSheet, closeBottomSheet } = useBottomSheet()
  const list = rebalanceSuggestions ?? []
  const hasRebalance = list.length > 0
  const hasDirect = directFundSuggestion != null
  const reinvestAmountStr = String(roundOffAndFormatNumberWithCurrency(totalWithdrawAmount ?? 0))
  const heading = getReinvestHeading(reinvestAmountStr, hasDirect, hasRebalance)
  const sameFund = toSameFundSuggestion(currentFundData)

  let options: ReinvestOption[]
  let separatorText = ''
  if (hasRebalance && hasDirect) {
    options = [
      ...list.map((fund) => ({ fund, isRecommended: true })),
      { fund: directFundSuggestion, isRecommended: false },
    ]
    separatorText = 'or reinvest in direct plan of the same fund'
  } else if (hasRebalance && !hasDirect) {
    options = [
      ...list.map((fund) => ({ fund, isRecommended: true })),
      { fund: sameFund, isRecommended: false },
    ]
    separatorText = 'or reinvest in same fund'
  } else if (hasDirect) {
    options = [{ fund: directFundSuggestion, isRecommended: false }]
  } else {
    options = [{ fund: sameFund, isRecommended: false }]
  }

  const rebalanceFundsLength = list.length

  const handleISINClick = (isin: string) => {
    if (isin) {
      trackTaxHarvestingUserClicked({
        screen: TaxHarvestingScreenName.Recommendation,
        eventId: TaxHarvestingEventId.ReinvestFundCard,
        fundIsin: isin
      })
      postCTA(JSON.stringify(getMutualFundDetailCTA({ isin, navigationStyle: 'PUSH' })))
    }
  }

  return (
    <div className="flex flex-col gap-3">
      <StepHeading
        stepNumber={stepNumber}
        title={heading}
      />
      <div
        onClick={() => {
          openBottomSheet(
            <ReinvestLowerAmountBottomSheet onDismiss={closeBottomSheet} />,
            closeBottomSheet
          )
        }}
        className='text-grey40 text-13 underline cursor-pointer'
      >
        Why is this lower than withdrawal amount?
      </div>
      <div className="flex flex-col gap-3 pt-4">
        {options.map((option, idx) => (
          <div key={String(option.fund?.isin + '_' + idx)} className="flex flex-col">
            {option.isRecommended && <RecommendedRebalancingStrip />}
            <div onClick={() => handleISINClick(option.fund?.isin ?? '')} className={option.isRecommended ? '-mt-[42px]' : ''}>
              <ReinvestFundCard
                fundLogo={option.fund?.logo ?? ''}
                fundTitle={option.fund?.name ?? ''}
                fundNature={option.fund?.fundNature ?? ''}
                fundCategory={option.fund?.fundCategory ?? ''}
                powerRank={option.fund?.powerRank ?? 0}
                powerRating={option.fund?.performanceRating}
              />
            </div>
            {getAlternativeHintText(option.fund?.performanceRating) && (
              <div
                onClick={() => handleISINClick(option.fund?.isin ?? '')}
                className='mt-2 flex items-center gap-3 rounded-[12px] border border-[#F0E8D8] p-4 cursor-pointer'
              >
                <div className='mt-1 flex h-4 w-4 items-center justify-center rounded-full bg-[#FFF2DF] text-[11px] text-[#CC5C2D]'>
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <g clip-path="url(#clip0_73822_14235)">
                      <path fill-rule="evenodd" clip-rule="evenodd" d="M8 16C13.3019 16 16 13.3019 16 8C16 2.69807 13.3019 0 8 0C2.69807 0 0 2.69807 0 8C0 13.3019 2.69807 16 8 16Z" fill="#FFF2DF" />
                      <path fill-rule="evenodd" clip-rule="evenodd" d="M6.37569 10.2069C6.00208 10.2069 5.69922 10.5023 5.69922 10.8667C5.69922 11.2312 6.00208 11.5266 6.37569 11.5266H9.62275C9.99635 11.5266 10.2992 11.2312 10.2992 10.8667C10.2992 10.5023 9.99635 10.2069 9.62275 10.2069H8.67569V7.17164C8.67569 6.80722 8.37283 6.51181 7.99922 6.51181H6.91687C6.54326 6.51181 6.2404 6.80722 6.2404 7.17164C6.2404 7.53606 6.54326 7.83148 6.91687 7.83148H7.32275V10.2069H6.37569ZM9.08157 4.5323C9.08157 5.11536 8.59698 5.58804 7.99922 5.58804C7.40146 5.58804 6.91687 5.11536 6.91687 4.5323C6.91687 3.94924 7.40146 3.47656 7.99922 3.47656C8.59698 3.47656 9.08157 3.94924 9.08157 4.5323Z" fill="#CC5C2D" />
                    </g>
                    <defs>
                      <clipPath id="clip0_73822_14235">
                        <rect width="16" height="16" fill="white" />
                      </clipPath>
                    </defs>
                  </svg>

                </div>
                <div className='text-[13px] font-[400] leading-[150%] text-[#6A6E6E]'>
                  {getAlternativeHintText(option.fund?.performanceRating)}{' '}
                  {/* <span className='font-[500] text-[#232323] underline'>View alternatives</span> */}
                </div>
              </div>
            )}
            {separatorText && idx === rebalanceFundsLength - 1 && (
              <p className="text-[14px] font-[400] text-[#8E9393] text-center pt-4 pb-1">
                {separatorText}
              </p>
            )}
          </div>
        ))}
      </div>

      {isElss && <ELSSCallout />}
    </div>
  )
}
